import React, { useEffect, useState } from "react";
import {
    Eye,
    Loader2,
    Megaphone,
    Save,
} from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";

const DEFAULT_TITLE = "Blackrod Now update";

export default function SiteAnnouncementAdminCard() {
    const {
        siteSettings,
        setSiteSettings,
    } = useApp();

    const [enabled, setEnabled] = useState(
        Boolean(siteSettings?.popup_enabled)
    );

    const [title, setTitle] = useState(
        siteSettings?.popup_title ||
            DEFAULT_TITLE
    );

    const [message, setMessage] = useState(
        siteSettings?.popup_message || ""
    );

    const [loading, setLoading] =
        useState(true);

    const [busy, setBusy] =
        useState(false);

    const [previewOpen, setPreviewOpen] =
        useState(false);

    useEffect(() => {
        setEnabled(
            Boolean(
                siteSettings?.popup_enabled
            )
        );

        setTitle(
            siteSettings?.popup_title !==
                undefined
                ? siteSettings.popup_title
                : DEFAULT_TITLE
        );

        setMessage(
            siteSettings?.popup_message || ""
        );
    }, [siteSettings]);

    useEffect(() => {
        let alive = true;

        const load = async () => {
            try {
                const latest =
                    await api.getSiteSettings();

                if (!alive || !latest) {
                    return;
                }

                setSiteSettings(latest);
            } catch {
                if (alive) {
                    toast.error(
                        "Could not load the current site pop-up settings"
                    );
                }
            } finally {
                if (alive) {
                    setLoading(false);
                }
            }
        };

        load();

        return () => {
            alive = false;
        };
    }, [setSiteSettings]);

    const saveMessage = async () => {
        const cleanMessage =
            message.trim();

        const cleanTitle =
            title.trim();

        if (!cleanMessage) {
            toast.error(
                "Add a message before saving the pop-up"
            );
            return;
        }

        setBusy(true);

        try {
            const updated =
                await api.updateSiteSettings({
                    popup_title:
                        cleanTitle,
                    popup_message:
                        cleanMessage,
                });

            setSiteSettings(updated);

            toast.success(
                enabled
                    ? "Pop-up updated and republished to visitors"
                    : "Pop-up text saved"
            );
        } catch (error) {
            toast.error(
                error?.response?.data
                    ?.detail ||
                    "Could not save the site pop-up"
            );
        } finally {
            setBusy(false);
        }
    };

    const toggleEnabled = async () => {
        const nextEnabled =
            !enabled;

        const cleanMessage =
            message.trim();

        const cleanTitle =
            title.trim();

        if (
            nextEnabled &&
            !cleanMessage
        ) {
            toast.error(
                "Add a message before enabling the site pop-up"
            );
            return;
        }

        setBusy(true);

        try {
            const updated =
                await api.updateSiteSettings({
                    popup_enabled:
                        nextEnabled,
                    popup_title:
                        cleanTitle,
                    popup_message:
                        cleanMessage,
                });

            setSiteSettings(updated);

            toast.success(
                nextEnabled
                    ? "Site pop-up enabled"
                    : "Site pop-up disabled"
            );
        } catch (error) {
            toast.error(
                error?.response?.data
                    ?.detail ||
                    "Could not change the pop-up status"
            );
        } finally {
            setBusy(false);
        }
    };

    const openPreview = () => {
        if (!message.trim()) {
            toast.error(
                "Add a message before previewing the pop-up"
            );
            return;
        }

        setPreviewOpen(true);
    };

    const liveTitle =
        title.trim() ||
        "Blackrod Now";

    const liveMessage =
        message.trim();

    return (
        <>
            <section
                className="rounded-3xl border border-border bg-surface p-5 shadow-sm sm:p-6"
                data-testid="site-announcement-admin-card"
            >
                <div className="flex flex-col gap-5 lg:flex-row lg:items-start lg:justify-between">
                    <div className="max-w-2xl">
                        <div className="flex flex-wrap items-center gap-3">
                            <h2 className="inline-flex items-center gap-2 text-lg font-extrabold">
                                <Megaphone className="h-5 w-5 text-primary" />

                                Site-wide pop-up
                            </h2>

                            <span
                                className={`rounded-full px-3 py-1 text-xs font-extrabold ${
                                    enabled
                                        ? "bg-emerald-100 text-emerald-800 dark:bg-emerald-950/50 dark:text-emerald-300"
                                        : "bg-muted text-muted-foreground"
                                }`}
                            >
                                {enabled
                                    ? "Visible to visitors"
                                    : "Currently off"}
                            </span>
                        </div>

                        <p className="mt-2 text-sm leading-6 text-muted-foreground">
                            Show one editable
                            announcement to visitors
                            across Blackrod Now. Once
                            a visitor closes it, it
                            stays closed for that
                            browsing session. Saving
                            a new live message creates
                            a new revision so the
                            announcement can appear
                            again.
                        </p>
                    </div>

                    <div className="flex items-center gap-3 rounded-2xl border border-border bg-background px-4 py-3">
                        <div>
                            <div className="text-sm font-bold">
                                Show to visitors
                            </div>

                            <div className="text-xs text-muted-foreground">
                                Change instantly
                            </div>
                        </div>

                        <button
                            type="button"
                            role="switch"
                            aria-checked={
                                enabled
                            }
                            aria-label={
                                enabled
                                    ? "Disable site pop-up"
                                    : "Enable site pop-up"
                            }
                            disabled={
                                busy ||
                                loading
                            }
                            onClick={
                                toggleEnabled
                            }
                            className={`relative inline-flex h-7 w-12 shrink-0 rounded-full transition ${
                                enabled
                                    ? "bg-primary"
                                    : "bg-muted-foreground/30"
                            } disabled:cursor-not-allowed disabled:opacity-50`}
                        >
                            <span
                                className={`absolute top-1 h-5 w-5 rounded-full bg-white shadow transition-transform ${
                                    enabled
                                        ? "translate-x-6"
                                        : "translate-x-1"
                                }`}
                            />
                        </button>
                    </div>
                </div>

                <div className="mt-5 grid gap-4">
                    <label className="grid gap-1.5">
                        <span className="text-sm font-bold">
                            Heading
                        </span>

                        <input
                            type="text"
                            value={title}
                            maxLength={120}
                            disabled={
                                busy ||
                                loading
                            }
                            onChange={(
                                event
                            ) =>
                                setTitle(
                                    event
                                        .target
                                        .value
                                )
                            }
                            placeholder="Blackrod Now update"
                            className="w-full rounded-xl border border-border bg-background px-3.5 py-2.5 text-sm outline-none focus:ring-2 focus:ring-primary/40 disabled:opacity-60"
                        />
                    </label>

                    <label className="grid gap-1.5">
                        <div className="flex items-center justify-between gap-3">
                            <span className="text-sm font-bold">
                                Message
                            </span>

                            <span className="text-xs text-muted-foreground">
                                {message.length}
                                /4000
                            </span>
                        </div>

                        <textarea
                            value={
                                message
                            }
                            maxLength={4000}
                            disabled={
                                busy ||
                                loading
                            }
                            onChange={(
                                event
                            ) =>
                                setMessage(
                                    event
                                        .target
                                        .value
                                )
                            }
                            placeholder="Type the message you want every visitor to see..."
                            className="min-h-[120px] w-full resize-y rounded-xl border border-border bg-background px-3.5 py-3 text-sm leading-6 outline-none focus:ring-2 focus:ring-primary/40 disabled:opacity-60"
                        />
                    </label>
                </div>

                <div className="mt-5 flex flex-wrap items-center gap-3">
                    <button
                        type="button"
                        onClick={
                            saveMessage
                        }
                        disabled={
                            busy ||
                            loading
                        }
                        className="inline-flex items-center gap-2 rounded-full bg-primary px-5 py-2.5 text-sm font-bold text-primary-foreground transition hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                        {busy ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                            <Save className="h-4 w-4" />
                        )}

                        Save message
                    </button>

                    <button
                        type="button"
                        onClick={
                            openPreview
                        }
                        disabled={
                            busy ||
                            loading
                        }
                        className="inline-flex items-center gap-2 rounded-full border border-border bg-background px-5 py-2.5 text-sm font-bold transition hover:bg-muted disabled:cursor-not-allowed disabled:opacity-50"
                    >
                        <Eye className="h-4 w-4" />

                        Preview
                    </button>

                    <span className="text-xs text-muted-foreground">
                        Revision{" "}
                        {Number(
                            siteSettings?.popup_revision ||
                                0
                        )}
                    </span>
                </div>
            </section>

            <Dialog
                open={previewOpen}
                onOpenChange={
                    setPreviewOpen
                }
            >
                <DialogContent className="overflow-hidden border-0 p-0 shadow-2xl sm:max-w-lg">
                    <div className="h-2 bg-[#D5FF00]" />

                    <div className="p-6 sm:p-7">
                        <div className="mb-5 flex items-center gap-3">
                            <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-primary text-primary-foreground">
                                <Megaphone className="h-5 w-5" />
                            </div>

                            <div>
                                <div className="text-[11px] font-extrabold uppercase tracking-[0.18em] text-primary">
                                    Blackrod
                                    Now
                                </div>

                                <DialogHeader className="space-y-0 text-left">
                                    <DialogTitle className="mt-1 text-xl font-extrabold leading-tight">
                                        {
                                            liveTitle
                                        }
                                    </DialogTitle>
                                </DialogHeader>
                            </div>
                        </div>

                        <div className="whitespace-pre-wrap text-sm leading-6 text-foreground/85 sm:text-[15px]">
                            {
                                liveMessage
                            }
                        </div>

                        <div className="mt-6 flex justify-end">
                            <button
                                type="button"
                                onClick={() =>
                                    setPreviewOpen(
                                        false
                                    )
                                }
                                className="inline-flex items-center justify-center rounded-full bg-primary px-6 py-2.5 text-sm font-bold text-primary-foreground"
                            >
                                Close
                            </button>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>
        </>
    );
}